import {
  Action,
  Block,
  PageList,
  PageListEntry,
  PageSet,
} from "src/pageDefinitions/types";
import {
  getPageAtLocation,
  getPageLocation,
  getPageSet,
  getPageSetForLayer,
  PageLocation,
} from "src/pageDefinitions/pageSets";
import invariant from "tiny-invariant";
import { CoreReduxState, CoreReduxStore } from "src/utils/redux/store";
import { conditionsPass } from "src/pageDefinitions/conditions";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { saveSurveyAnswers } from "src/utils/redux/slices/userData";
import { visitPageSet } from "src/pageDefinitions/pageSets/visit";
import { captureMessage } from "@sentry/minimal";

/**
 * Finds all survey-related pages that are defined within page set at given point.
 */

export function getPageSetSurvey(
  pageSet: PageSet,
  page: PageListEntry,
  state?: CoreReduxState
): {
  name: string;
  surveyNameSpace: string;
  loggedInHeader: boolean;
  blockLocation: PageLocation;
  pages?: PageList;
  props: Block["props"];
  onAnswered?: ReadonlyArray<Action>;
} {
  const location = pageSet && page && getPageLocation(pageSet, page);
  if (!location) {
    return {
      name: undefined,
      blockLocation: undefined,
      surveyNameSpace: "surveyAnswers",
      loggedInHeader: false,
      props: {},
    };
  }

  const blockLocation = location.slice(0, 1);
  const block = getPageAtLocation(pageSet, blockLocation);
  invariant(
    "name" in block,
    `No survey block found wrapping location ${location}`
  );
  invariant(block.name, `Survey block as location ${location} has no name`);

  // Include pages if a state is passed to eval against.
  let pages;
  if (state) {
    pages = block.block.filter((pageToFilter) =>
      conditionsPass(pageToFilter.conditions, state)
    );
  }

  return {
    name: block.name,
    surveyNameSpace: block.surveyNameSpace || "surveyAnswers",
    loggedInHeader: block.loggedInHeader,
    blockLocation: location.slice(0, -1),
    pages,
    props: block.props || {},
    onAnswered: block.onAnswered,
  };
}

/**
 * Finds all the pages in this survey, accounting for layered page sets. We consider a page set to
 * be "in the same survey" if it has an "exit layer" call; meaning it would like to pop the stack
 * to the previous page set.
 *
 * This only deals with pages, because `name`, `namespace`, `props` etc could be different for all
 * the page sets involved.
 */
export async function getFlattenedPageSetSurveyPages(
  pageSet: PageSet,
  page: PageListEntry,
  state: CoreReduxState,
  opts?: { onlyDown?: boolean }
) {
  let workingPageSet = pageSet;
  let workingPage = page;
  // To prevent infinite recursion. In recursed calls, we don't consider our parent layer, just
  // potential children.
  if (!opts?.onlyDown) {
    const rootLayer = findLayerRoot(pageSet, page, state);
    workingPageSet = rootLayer.pageSet;
    workingPage = rootLayer.page;
  }
  // Convenient to use `getPageSetSurvey` here since it already checks conditions. More iteration
  // overall, though.
  const surveyPages =
    Array.from(getPageSetSurvey(workingPageSet, workingPage, state).pages) ||
    [];
  // iterating backwards so we can splice midloop
  for (let i = surveyPages.length - 1; i >= 0; i--) {
    const entry = surveyPages[i];
    if ("goto" in entry) {
      const layerCall = entry.goto?.layer || entry.goto?.pushLayer;
      if (layerCall) {
        // Grab this "child" layer, so we can include all its pages as well
        // eslint-disable-next-line no-await-in-loop
        const innerPageSet = await getPageSetForLayer(layerCall[0], state);
        // Recurse, since there could be more layers in there as well
        // eslint-disable-next-line no-await-in-loop
        const innerPages = await getFlattenedPageSetSurveyPages(
          innerPageSet,
          innerPageSet.pages[0],
          state,
          { onlyDown: true }
        );
        if (hasExitCall(innerPageSet)) {
          // Remove this goto entry and add in all the inner pages in its place
          surveyPages.splice(i, 1, ...innerPages);
        }
      }
    }
  }
  return surveyPages;
}

/**
 * Finds our "root" layer by walking down any the `exitLayer` calls. Relies on history, since a
 * pageSet by itself doesn't know who its parent is.
 */
function findLayerRoot(
  pageSet: PageSet,
  page: PageListEntry,
  state: CoreReduxState
): {
  pageSet: PageSet;
  page: PageListEntry;
} {
  const exitCall = hasExitCall(pageSet);
  if (exitCall) {
    const { history } = state.linearBuyflow;
    const historyIndex = history?.findIndex(
      (item) => item.pageSetId === pageSet.id
    );
    if (historyIndex < 1) {
      captureMessage("Layer exit found, but history missing", {
        contexts: {
          findLayerRoot: {
            pageSet: pageSet.id,
          },
        },
      });
      return {
        pageSet,
        page,
      };
    }
    const prevPageSetId = history[historyIndex - 1].pageSetId;
    const prevPageSet = getPageSet(prevPageSetId);
    const sourceLocation = findSourceLocation(
      prevPageSet,
      history[historyIndex].sourceId
    );
    const sourcePage = getPageAtLocation(prevPageSet, sourceLocation);
    return findLayerRoot(prevPageSet, sourcePage, state);
  }
  return {
    pageSet,
    page,
  };
}

function hasExitCall(pageSet: PageSet) {
  let exitCall;
  visitPageSet(pageSet, ({ entry }) => {
    if ("goto" in entry) {
      const thisExit = entry.goto?.exitLayer;
      if (thisExit) {
        // Sanity logging -- there should probably be only one of these
        if (exitCall) {
          captureMessage("Multiple exits found", {
            contexts: {
              hasExitCall: {
                pageSet: pageSet.id,
              },
            },
          });
        }
        exitCall = thisExit;
      }
    }
  });
  return exitCall;
}

export async function saveSurveyAnswersInUserData(
  store: CoreReduxStore,
  surveyNameSpace: string,
  surveyAnswers: SurveyAnswersState,
  dateKey?: string
) {
  // saveSurveyAnswers stores in userData slice. This check ensures that the JSON config
  // will properly load that data in the future/on refresh.
  invariant(
    surveyNameSpace.startsWith("userData."),
    "surveyNameSpace must start with userData for this action."
  );

  await store.dispatch(
    saveSurveyAnswers(surveyAnswers, {
      surveyNameSpace: surveyNameSpace.replace("userData.", ""),
      dateKey,
    })
  );
}

export function findSourceLocation(pageSet: PageSet, sourceId: string) {
  // If a source id was passed, jump to that call before moving to the next
  let sourceLocation: PageLocation;
  if (sourceId) {
    visitPageSet(pageSet, ({ entry }) => {
      if ("goto" in entry) {
        const layerCall = entry.goto.layer || entry.goto.pushLayer;
        if (layerCall && layerCall.includes(sourceId)) {
          // Sanity logging
          if (sourceLocation) {
            captureMessage("Multiple source locations found", {
              contexts: {
                pushLayer: {
                  sourceId,
                  pageSet: pageSet.id,
                },
              },
            });
          }

          sourceLocation = getPageLocation(pageSet, entry);
        }
      }
    });

    // Sanity logging
    if (!sourceLocation) {
      captureMessage("sourceId not found", {
        contexts: {
          pushLayer: {
            sourceId,
            pageSet: pageSet.id,
          },
        },
      });
    }
  }
  return sourceLocation;
}

// Gets key-value pairs for survey legend.
// Survey legend is used for score questions, explaining what low and high scores mean for the particular question
export function getSurveyLegendWithTranslationKeys(
  questionId,
  overrides?: {
    namespace?: string;
    type?: string;
  }
) {
  return getSurveyOptionsWithTranslationKeys(questionId, ["left", "right"], {
    ...overrides,
    type: "legend",
  });
}

// Gets key-value pairs for survey question options.
export function getSurveyOptionsWithTranslationKeys<
  T extends string,
  U extends string
>(
  questionId: string,
  options: T[],
  overrides?: {
    namespace?: string;
    type?: string;
  }
): Record<T & U, string> {
  const path = { namespace: "survey:questions", type: "options", ...overrides };
  const translated: Record<string, string> = {};
  options.forEach((option) => {
    translated[
      option
    ] = `t:${path.namespace}:${questionId}:${path.type}:${option}`;
  });
  return translated;
}

export const HmNamespace = "surveyHM:questions";
